import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  Target,
  Lock,
  CheckCircle,
  PlayCircle,
  ChevronRight,
  RefreshCw,
  BookOpen,
  Crown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  api,
  PersonalizedRoadmap as PRoadmap,
  PersonalizedRoadmapWeek,
  PersonalizedRoadmapModule,
  UserOnboarding,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { featureFlags, userHasPremiumAccess } from "@/config/featureFlags";

const STATUS_ICONS = {
  locked: Lock,
  available: PlayCircle,
  in_progress: PlayCircle,
  completed: CheckCircle,
};

const STATUS_COLORS = {
  locked: "text-slate-500 bg-slate-800 border-slate-700",
  available: "text-indigo-400 bg-indigo-900/30 border-indigo-500",
  in_progress: "text-amber-400 bg-amber-900/30 border-amber-500",
  completed: "text-green-400 bg-green-900/30 border-green-500",
};

export default function PersonalizedRoadmap() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremiumUser = userHasPremiumAccess(user);
  const [roadmap, setRoadmap] = useState<PRoadmap | null>(null);
  const [onboarding, setOnboarding] = useState<UserOnboarding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const showPremiumGate = featureFlags.CHARGEBEE_BILLING && !isPremiumUser;

  useEffect(() => {
    if (showPremiumGate) return;
    const loadData = async () => {
      try {
        const [roadmapData, onboardingData] = await Promise.all([
          api.getPersonalizedRoadmap(),
          api.getOnboarding(),
        ]);

        if (!roadmapData) {
          // No roadmap yet, redirect to onboarding
          navigate("/onboarding/goals");
          return;
        }

        setRoadmap(roadmapData);
        setOnboarding(onboardingData);
      } catch (err: any) {
        setError(err.message || "Failed to load roadmap");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [navigate, showPremiumGate]);

  // Show premium gate for non-premium users
  if (showPremiumGate) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-slate-900 border-slate-700">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-yellow-900/30 rounded-full w-fit">
              <Crown className="w-8 h-8 text-yellow-400" />
            </div>
            <CardTitle className="text-2xl text-white">My Path is a Premium Feature</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-slate-400">
              Get a personalized learning roadmap tailored to your goals and skill level.
              Upgrade to premium to unlock this feature.
            </p>
            <div className="space-y-2 text-left bg-slate-800 rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Lock className="w-4 h-4 text-yellow-500" />
                <span>AI-generated personalized curriculum</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Lock className="w-4 h-4 text-yellow-500" />
                <span>Week-by-week learning schedule</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-300">
                <Lock className="w-4 h-4 text-yellow-500" />
                <span>Progress tracking & mastery levels</span>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 border-slate-600 text-slate-300" onClick={() => navigate("/arena")}>
                Back to Arena
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                onClick={() => navigate("/pricing")}
              >
                View Plans
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleStartModule = (moduleId: string) => {
    // Navigate to the module view with fromRoadmap flag
    navigate(`/my-roadmap/module/${moduleId}?fromRoadmap=true`);
  };

  const handleRetakeAssessment = () => {
    navigate("/assessment");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="text-gray-500 dark:text-gray-400">
            Loading your roadmap...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-red-500">{error}</p>
          <Button onClick={() => navigate("/dashboard")} variant="outline">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (!roadmap) return null;

  // Calculate stats
  const totalModules = roadmap.weeks.reduce(
    (sum, week) => sum + week.modules.length,
    0,
  );
  const completedModules = roadmap.weeks.reduce(
    (sum, week) =>
      sum + week.modules.filter((m) => m.status === "completed").length,
    0,
  );

  const HOURS_LABELS: Record<number, string> = {
    1: "1 hr/week",
    2: "2 hrs/week",
    3: "3 hrs/week",
    5: "5+ hrs/week",
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Your Learning Roadmap
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Personalized path based on your assessment results
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleRetakeAssessment}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Retake Assessment
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <Calendar className="w-6 h-6 text-indigo-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {roadmap.total_weeks}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Weeks
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {totalModules}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Modules
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {completedModules}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Completed
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-amber-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {onboarding?.weekly_hours
                  ? HOURS_LABELS[onboarding.weekly_hours]
                  : "3 hrs/week"}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Commitment
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Timeline */}
        <div className="space-y-6">
          {roadmap.weeks.map((week) => (
            <WeekCard
              key={week.week}
              week={week}
              onStartModule={handleStartModule}
            />
          ))}
        </div>

        {/* Back to Dashboard */}
        <div className="flex justify-center">
          <Link to="/dashboard">
            <Button variant="outline">Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

interface WeekCardProps {
  week: PersonalizedRoadmapWeek;
  onStartModule: (moduleId: string) => void;
}

function WeekCard({ week, onStartModule }: WeekCardProps) {
  const completedCount = week.modules.filter(
    (m) => m.status === "completed",
  ).length;
  const isCurrentWeek = week.modules.some(
    (m) => m.status === "available" || m.status === "in_progress",
  );

  return (
    <Card
      className={`bg-white dark:bg-gray-800 border-2 ${
        isCurrentWeek
          ? "border-indigo-500 dark:border-indigo-400"
          : "border-gray-200 dark:border-gray-700"
      }`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Timeline dot */}
            <div
              className={`w-4 h-4 rounded-full ${
                completedCount === week.modules.length
                  ? "bg-green-500"
                  : isCurrentWeek
                    ? "bg-indigo-500 animate-pulse"
                    : "bg-gray-300 dark:bg-gray-600"
              }`}
            />
            <div>
              <CardTitle className="text-lg text-gray-900 dark:text-white">
                Week {week.week}: {week.theme}
              </CardTitle>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {completedCount}/{week.modules.length} modules completed
              </p>
            </div>
          </div>
          {isCurrentWeek && (
            <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
              Current
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="grid gap-3">
          {week.modules.map((module) => (
            <ModuleRow
              key={module.module_id}
              module={module}
              onStart={() => onStartModule(module.module_id)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

interface ModuleRowProps {
  module: PersonalizedRoadmapModule;
  onStart: () => void;
}

function ModuleRow({ module, onStart }: ModuleRowProps) {
  const Icon = STATUS_ICONS[module.status];
  const isClickable =
    module.status === "available" || module.status === "in_progress";

  return (
    <div
      className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
        STATUS_COLORS[module.status]
      } ${isClickable ? "cursor-pointer hover:scale-[1.01]" : ""}`}
      onClick={isClickable ? onStart : undefined}
    >
      <div className="flex items-center gap-3">
        <Icon
          className={`w-5 h-5 ${
            module.status === "locked" ? "text-slate-500" : ""
          }`}
        />
        <div>
          <span
            className={`font-medium ${
              module.status === "locked"
                ? "text-slate-500"
                : "text-gray-900 dark:text-white"
            }`}
          >
            {module.module_name}
          </span>
          <div className="flex items-center gap-2 mt-1">
            <Badge
              variant="outline"
              className="text-xs bg-slate-500/20 text-slate-300"
            >
              <BookOpen className="w-3 h-3 mr-1" />
              {module.roadmap_name}
            </Badge>
          </div>
        </div>
      </div>
      {isClickable && <ChevronRight className="w-5 h-5 text-gray-400" />}
    </div>
  );
}
